import { useEffect, useMemo, useState } from "react";
import { Button } from "@oc-labs/wt-storybook";
import { Plus, Search, Braces, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import UpsertAbiModal from "../../../components/modals/abi/UpsertAbiModal";
import GenerateAbiModal from "../../../components/modals/abi/GenerateAbiModal";
import DeleteModal from "../../../components/modals/DeleteModal";
import useModal from "../../../hooks/useModal";
import useAbis from "../../../hooks/useAbis";
import useSourceCode from "../../../hooks/useSourceCode";

const formatBytes = (bytes) => {
  if (bytes == null || isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

export default function AdminAbis() {
  const { adminAbis, adminDeleteAbi } = useAbis();
  const { adminSourceCodes } = useSourceCode();
  const { open: openUpsertModal } = useModal("upsert-abi-modal");
  const { open: openGenerateModal } = useModal("generate-abi-modal");
  const { open: openDeleteModal } = useModal("delete-modal");
  const [items, setItems] = useState([]);
  const [sourceCodes, setSourceCodes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    loadSourceCodes();
  }, []);

  const load = () => {
    setLoading(true);
    adminAbis()
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.response?.data?.message || err.message || "Failed to load ABIs"))
      .finally(() => setLoading(false));
  };

  const loadSourceCodes = () => {
    adminSourceCodes()
      .then((res) => setSourceCodes(Array.isArray(res) ? res : []))
      .catch(() => {});
  };

  const handleDelete = (item) => {
    openDeleteModal({
      title: "Delete ABI",
      text: `Delete "${item.name}"? This action cannot be undone.`,
      onDelete: async () => {
        try {
          await adminDeleteAbi(item.name);
          toast.success("ABI deleted");
          load();
        } catch (err) {
          toast.error(err?.response?.data?.message || err.message || "Failed to delete");
        }
      },
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name?.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <>
      <UpsertAbiModal onUpsert={load} />
      <GenerateAbiModal onGenerated={load} sourceCodes={sourceCodes} />
      <DeleteModal />
      <div className="2xl:p-6 p-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Braces className="text-blue-600" />
            <h1 className="2xl:text-2xl text-lg font-bold text-gray-900">ABIs</h1>
            <span className="text-sm text-gray-500 ml-2">{items.length} ABIs</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="tertiary"
              size="small"
              onClick={() => openGenerateModal()}
              icon={<Sparkles size={16} />}
              className="whitespace-nowrap"
            >
              Auto-generate
            </Button>
            <Button
              type="secondary"
              size="small"
              onClick={() => openUpsertModal({ __isNew: true })}
              icon={<Plus size={16} />}
              className="whitespace-nowrap"
            >
              New ABI
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-10 pl-9 pr-4 text-sm"
            />
          </div>
        </div>

        <div className="rounded-lg shadow overflow-hidden">
          <table className="w-full bg-white table-fixed">
            <thead className="bg-gray-100 uppercase">
              <tr>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[40%]">Name</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]">Size</th>
                <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[25%]">Last modified</th>
                <th className="py-3 px-6 text-left text-sm font-medium tracking-wider w-[20%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="py-8 px-6 text-center text-gray-500" colSpan={4}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td className="py-8 px-6 text-center text-gray-500" colSpan={4}>
                    No ABIs found.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((item) => (
                <tr key={item.name} className="hover:bg-gray-50 transition">
                  <td className="py-4 px-6 text-sm font-medium text-gray-900 font-mono truncate">{item.name}</td>
                  <td className="py-4 px-6 text-sm text-gray-700 truncate">{formatBytes(item.size)}</td>
                  <td className="py-4 px-6 text-sm text-gray-500 truncate">{formatDate(item.lastModified)}</td>
                  <td className="py-4 px-6 text-sm">
                    <div className="flex flex-col gap-1.5 2xl:flex-row 2xl:items-center 2xl:gap-2">
                      <Button onClick={() => openUpsertModal(item)} size="small" type="primary">
                        View / Edit
                      </Button>
                      <Button onClick={() => handleDelete(item)} size="small" type="danger">
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
